import React, { useContext, useEffect, useState } from 'react';
import { fetchFinancialData, fetchWalletData, recordTransaction, logRecentActivity, recordRecentActivity } from '../../api/apiService';
import { AuthContext } from '../../context/AuthContext';
import LoadingSpinner from '../common/Loader';
import { Button } from '../common/Button';

const TransferScreen = () => {
    const [accounts, setAccounts] = useState([]);
    const [wallets, setWallets] = useState([]);
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [transferStatus, setTransferStatus] = useState(null);
    const [transferMessage, setTransferMessage] = useState('');
    const [transferAmount, setTransferAmount] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [error, setError] = useState(null); // New state for validation errors

    useEffect(() => {
        if (user) {
            const { access_token, user_id } = user;

            fetchFinancialData(access_token, user_id).then((response) => {
                if (!response.error && response.data.trading_accounts) {
                    const realAccounts = response.data.trading_accounts.filter(
                        trading_accounts => trading_accounts.account_mode !== "Demo"
                    );
                    setAccounts(realAccounts);
                }
            });

            fetchWalletData(access_token, user_id).then((response) => {
                if (!response.error && response.data.wallets) {
                    setWallets(response.data.wallets);
                }
            });
        }
    }, [user]);

    // Combine accounts and wallets into a single array
    const allAccountsAndWallets = [
        ...accounts.map(account => ({
            id: account.account_id,
            balance: account.balance,
            currency: account.currency,
            type: 'account'
        })),
        ...wallets.map(wallet => ({
            id: wallet.wallet_number,
            balance: wallet.balance,
            currency: wallet.currency,
            type: 'wallet'
        }))
    ];

    // Get balance of a specific account/wallet
    const getBalance = (id, type) => {
        const item = allAccountsAndWallets.find(item => item.id === id && item.type === type);
        return item ? parseFloat(item.balance) : 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null); // Reset previous errors

        if (!user || !user.access_token || !user.user_id) {
            setError('User is not authenticated. Please log in.');
            return;
        }

        const fromAccountElement = event.target.fromAccount;
        const toAccountElement = event.target.toAccount;
        const amountElement = event.target.amount;

        const fromAccountId = fromAccountElement.value;
        const fromAccountType = fromAccountElement.options[fromAccountElement.selectedIndex].getAttribute('data-type');
        const toAccountId = toAccountElement.value;
        const toAccountType = toAccountElement.options[toAccountElement.selectedIndex].getAttribute('data-type');
        const amount = parseFloat(amountElement.value);

        // Validation checks
        if (fromAccountId === toAccountId && fromAccountType === toAccountType) {
            setError('You cannot transfer funds from the same account/wallet to itself.');
            return;
        }

        if (isNaN(amount) || amount <= 0) {
            setError('Please enter a valid positive amount.');
            return;
        }

        // Check if balance is sufficient
        const fromBalance = getBalance(fromAccountId, fromAccountType);
        if (amount > fromBalance) {
            setError(`Insufficient funds. Your current balance is ${fromBalance}.`);
            return;
        }

        setIsLoading(true);

        const transactionData = {
            transaction_id: `TXN_${Date.now()}`,
            user_id: user.user_id,
            amount: amount,
            transaction_type: 'Transfer',
            from_type: fromAccountType,
            from_id: fromAccountId,
            to_type: toAccountType,
            to_id: toAccountId,
            status: "success",
            date: Date.now()
        };

        try {
            const response = await recordTransaction(user.access_token, transactionData);

            if (response.error) {
                setTransferStatus('failure');
                setTransferMessage(response.message || 'Transfer failed. Please try again.');
            } else {
                setTransferStatus('success');
                setTransferMessage(`Transfer of ${amount} USD was successful.`);
                setTransferAmount(amount);

                // Log recent activity on success
                const recentActivityData = {
                    userId: user.user_id,
                    description: `Your transfer transaction is successful. Transaction ID: ${transactionData.transaction_id}`,
                    date: new Date().toISOString(),
                    status: "success"
                };

                const activityResponse = await recordRecentActivity(user.access_token, recentActivityData);
                if (activityResponse.error) {
                    console.error('Failed to log recent activity:', activityResponse.message);
                }
            }
        } catch (error) {
            console.error('Error during transfer:', error);
            setTransferStatus('failure');
            setTransferMessage('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
            setModalOpen(true);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        if (transferStatus === 'success') {
            window.location.reload();
        }
    };

    return (
        <>
            {isLoading && <LoadingSpinner />}
            <section className="p-5">
                <div className="w-full bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-2xl font-bold mb-6">Transfer Funds</h2>
                    
                    {/* Error message display */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                            {error}
                        </div>
                    )}
                    
                    <form id="transferForm" onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label htmlFor="fromAccount" className="block text-sm font-bold text-gray-700">From:</label>
                            <select 
                                id="fromAccount" 
                                name="fromAccount" 
                                required 
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            >
                                <option value="">Select source account/wallet</option>
                                {allAccountsAndWallets.map((item, index) => (
                                    <option key={index} value={item.id} data-type={item.type}>
                                        {item.id} ({item.type.charAt(0).toUpperCase() + item.type.slice(1)}) - {item.currency} {item.balance}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-4">
                            <label htmlFor="toAccount" className="block text-sm font-bold text-gray-700">To:</label>
                            <select 
                                id="toAccount" 
                                name="toAccount" 
                                required 
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            >
                                <option value="">Select destination account/wallet</option>
                                {allAccountsAndWallets.map((item, index) => (
                                    <option key={index} value={item.id} data-type={item.type}>
                                        {item.id} ({item.type.charAt(0).toUpperCase() + item.type.slice(1)}) - {item.currency} {item.balance}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-4">
                            <label htmlFor="amount" className="block text-sm font-bold text-gray-700">Amount:</label>
                            <input 
                                type="number" 
                                id="amount" 
                                name="amount" 
                                placeholder="Enter amount" 
                                required 
                                min="0.01" 
                                step="0.01" 
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
                            />
                        </div>
                        <div className="flex justify-center">
                            <Button type="submit" width='w-64'>
                                Transfer
                            </Button>
                        </div>
                    </form>
                </div>

                {modalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
                        <div className="bg-white p-6 rounded-lg text-center">
                            <span className="close cursor-pointer text-xl font-bold" onClick={closeModal}>&times;</span>
                            <div className="modal-icon">
                                {transferStatus === 'success' ? (
                                    <i className="fas fa-check-circle text-green-500 text-5xl"></i>
                                ) : (
                                    <i className="fas fa-times-circle text-red-500 text-5xl"></i>
                                )}
                            </div>
                            <h2 className="text-2xl font-bold mt-4">{transferStatus === 'success' ? 'Success!' : 'Failed!'}</h2>
                            <p className="mt-2">{transferMessage}</p>
                            <button 
                                onClick={closeModal} 
                                className={`mt-4 px-4 py-2 ${transferStatus === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white rounded-md`}
                            >
                                {transferStatus === 'success' ? 'OK' : 'Try Again'}
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </>
    );
};

export default TransferScreen;